import { snapPointToSegment, nameShape } from "@logic/lib/2d"
import { screenToStudio } from "@logic/lib/design"
import { useRecoilTransaction_UNSTABLE } from "recoil"

import { nameNode } from "~/containers/LegacyDesigner/utils"

import type { TransactionOperation } from "."
import { readonlyTransactors } from "."
import { addEdge, tryToAddEdge } from "./addEdge"
import { addNode } from "./addNode"
import { surgicallyDeleteEdge } from "./deleteEdge"
import { getPreviewNodeSnapPoint } from "./previewEdgeLineLocking"
import { zoomState } from "../../view"
import { findEdgeState } from "../edges"
import { findNodeState } from "../nodes"
import { nodeSelectionState_INTERNAL } from "../nodeSelection"
import { divergeTimeline } from "../timeline/transactions"

export const segmentEdge: TransactionOperation<string> = (transactors, id) => {
  const { get, set } = transactors
  const edgeState = findEdgeState(id)
  const edge = get(edgeState)
  const { node1id, node2id } = edge
  const edgeDeletion = surgicallyDeleteEdge(transactors, id)
  const newNodeId = nameNode()

  const p1 = get(findNodeState(node1id))
  const p2 = get(findNodeState(node2id))
  const nodeSnapPosition = getPreviewNodeSnapPoint(readonlyTransactors(get))
  const zoom = get(zoomState)
  const studioPoint = screenToStudio.point(nodeSnapPosition.previewNode, zoom)
  const closestPoint = snapPointToSegment({ p1, p2 }, studioPoint)

  const nodeCreation = addNode(transactors, {
    id: newNodeId,
    payload: closestPoint,
  })

  const edgeCreation1 = addEdge(transactors, {
    id: nameShape(node1id, newNodeId),
    payload: { node1id, node2id: newNodeId },
  })

  const edgeCreation2 = addEdge(transactors, {
    id: nameShape(node2id, newNodeId),
    payload: { node1id: newNodeId, node2id },
  })

  const edgeCreation0 = tryToAddEdge(transactors, newNodeId)

  const changes = [
    edgeDeletion,
    nodeCreation,
    edgeCreation0,
    edgeCreation1,
    edgeCreation2,
  ]
  divergeTimeline(transactors, changes)
  set(nodeSelectionState_INTERNAL, new Set([newNodeId]))
}

export const useSegmentEdge = (id: string): VoidFunction =>
  useRecoilTransaction_UNSTABLE(
    (transactors) => () => segmentEdge(transactors, id),
  )
